// Impresión de comprobante de venta en 3 formatos: A4, 80mm y 60mm (como en Ferretería).
// Un solo layout de ticket, con CSS distinto por formato (ancho de página + tamaño de fuente).

function fmt2(n) {
  return parseFloat(n || 0).toFixed(2);
}

// Normaliza un ítem (venido del carrito de Nueva Venta o de sale_details en un
// reimpresión) a una forma única: qué cantidad/unidad mostrar y a qué precio.
// Prioridad: presentación de venta (ej. "2 CASILLERO") > doble variable
// KG+UND (ej. "5.000 KG + 3 UND") > unidad simple (ej. "10.000 UN").
export function normalizeTicketItem(it) {
  const presentationName = it.presentation_name || it.presentationName || null;
  const hasPresentation = presentationName &&
    (it.quantity_presentation != null || (it.factor && it.factor > 1));
  if (hasPresentation) {
    return {
      product_name: it.product_name,
      total:        it.total,
      quantity:     it.quantity_presentation != null ? it.quantity_presentation : it.quantity,
      unit_label:   presentationName,
      unit_price:   it.unit_price_presentation != null ? it.unit_price_presentation : it.unit_price,
      extra_qty_unidad: null,
    };
  }
  const qtyUnidad = parseFloat(it.qty_unidad || 0);
  return {
    product_name: it.product_name,
    total:        it.total,
    quantity:     it.quantity,
    unit_label:   it.unit_measure || it.baseUnitMeasure || '',
    unit_price:   it.unit_price,
    extra_qty_unidad: (!!it.special_condition && qtyUnidad > 0) ? qtyUnidad : null,
  };
}

/** Texto "cantidad unidad[ + unidades UND]" para un ítem ya normalizado */
export function ticketQtyUnitText(n) {
  const base = `${fmt2(n.quantity)} ${n.unit_label}`.trim();
  return n.extra_qty_unidad != null ? `${base} + ${parseInt(n.extra_qty_unidad, 10)} UND` : base;
}

function fmtSeqStr(n) {
  const s = String(n ?? '');
  return /^\d+$/.test(s) ? s.padStart(7, '0') : s;
}

/** Convierte un monto a letras en soles (hasta millones) */
function numberToWords(amount) {
  const UNIDADES = ['', 'UNO', 'DOS', 'TRES', 'CUATRO', 'CINCO', 'SEIS', 'SIETE', 'OCHO', 'NUEVE'];
  const DECENAS  = ['', 'DIEZ', 'VEINTE', 'TREINTA', 'CUARENTA', 'CINCUENTA', 'SESENTA', 'SETENTA', 'OCHENTA', 'NOVENTA'];
  const ESPECIALES = { 11:'ONCE',12:'DOCE',13:'TRECE',14:'CATORCE',15:'QUINCE',
    16:'DIECISÉIS',17:'DIECISIETE',18:'DIECIOCHO',19:'DIECINUEVE' };
  const CENTENAS = ['','CIENTO','DOSCIENTOS','TRESCIENTOS','CUATROCIENTOS',
    'QUINIENTOS','SEISCIENTOS','SETECIENTOS','OCHOCIENTOS','NOVECIENTOS'];

  function cientos(n) {
    if (n === 0) return '';
    if (n === 100) return 'CIEN';
    const c = Math.floor(n / 100), resto = n % 100;
    const centStr = CENTENAS[c];
    if (resto === 0) return centStr;
    if (ESPECIALES[resto]) return (centStr ? centStr + ' ' : '') + ESPECIALES[resto];
    const dec = Math.floor(resto / 10), uni = resto % 10;
    const parts = [];
    if (centStr) parts.push(centStr);
    if (dec > 0) parts.push(DECENAS[dec]);
    if (uni > 0) parts.push(UNIDADES[uni]);
    return parts.join(' Y ');
  }
  function miles(n) {
    if (n === 0) return '';
    const m = Math.floor(n / 1000), resto = n % 1000;
    const milStr = m === 1 ? 'MIL' : (m > 0 ? cientos(m) + ' MIL' : '');
    const restoStr = cientos(resto);
    return [milStr, restoStr].filter(Boolean).join(' ');
  }
  const entero = Math.floor(amount);
  const centavos = Math.round((amount - entero) * 100);
  const palabras = entero === 0 ? 'CERO' : miles(entero);
  return `${palabras} CON ${String(centavos).padStart(2, '0')}/100 SOLES`;
}

const CSS_BY_FMT = {
  a4: `
    @page { size: A4; margin: 15mm; }
    body { font-family: Arial, Helvetica, sans-serif; font-size: 12px; max-width: 480px; margin: 0 auto; }
    .tk-doc { font-size: 15px; }
    .tk-items-head { font-size: 12px; }
    .tk-item-name { font-size: 12px; }
    .tk-item-line { font-size: 11.5px; }
    .tk-total-row.grand { font-size: 16px; }
    .tk-item-oneline {
      display: flex; align-items: baseline; gap: 10px;
      padding: 3px 0; border-bottom: 1px dotted #ccc;
    }
    .tk-item-oneline .tk-item-name {
      flex: 1 1 auto; min-width: 0; font-weight: 600;
      overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
    }
    .tk-item-oneline .tk-item-qty { flex-shrink: 0; color: #333; white-space: nowrap; }
    .tk-item-oneline .tk-item-total { flex-shrink: 0; font-weight: 700; min-width: 66px; text-align: right; }
  `,
  '80': `
    @page { size: 80mm auto; margin: 4mm; }
    /* font-weight:700 en el body — en impresoras térmicas de 80mm el texto
       normal (400) sale casi ilegible; negrita se imprime sólida. */
    body { font-family: 'Courier New', Courier, monospace; font-size: 12px; width: 72mm; margin: 0 auto; font-weight: 700; }
  `,
  '60': `
    @page { size: 60mm auto; margin: 3mm; }
    body { font-family: 'Courier New', Courier, monospace; font-size: 9.5px; width: 52mm; margin: 0 auto; }
    .tk-empresa-nombre { font-size: 11px; }
    .tk-tklbl { min-width: 55px; }
    .tk-items-head { font-size: 8.5px; }
    .tk-item-line { font-size: 9px; }
  `,
};

function buildHtml({ empresa, serie, secuencia, client_name, document_number, date, seller, items, payments, subtotal, discount, total, notes }, fmt) {
  // En A4 sobra espacio horizontal, así que cada producto va en una sola
  // línea (nombre + cantidad x precio + total); en 80mm/60mm (papel
  // térmico angosto) se mantiene el formato de dos líneas.
  const itemsHtml = (items || []).map(raw => {
    const it = normalizeTicketItem(raw);
    if (fmt === 'a4') {
      return `
      <div class="tk-item-block tk-item-oneline">
        <span class="tk-item-name">${it.product_name || ''}</span>
        <span class="tk-item-qty">${ticketQtyUnitText(it)} x S/ ${fmt2(it.unit_price)}</span>
        <span class="tk-item-total">S/ ${fmt2(it.total)}</span>
      </div>`;
    }
    return `
    <div class="tk-item-block">
      <div class="tk-item-name">${it.product_name || ''}</div>
      <div class="tk-item-line">
        <span>${ticketQtyUnitText(it)} x S/ ${fmt2(it.unit_price)}</span>
        <span style="text-align:right">S/ ${fmt2(it.total)}</span>
      </div>
    </div>`;
  }).join('');

  const formaPago = (payments || []).map(p => p.method).filter(Boolean).join(', ') || '-';
  const fechaTxt = date ? new Date(date).toLocaleString('es-PE', { timeZone: 'America/Lima' }) : new Date().toLocaleString('es-PE', { timeZone: 'America/Lima' });
  // Tipo de venta (CONTADO/CRÉDITO) — se deriva de `payments`, que ya trae
  // 'CRÉDITO' o 'NOTA DE CRÉDITO' como método cuando la venta es al crédito
  // (ver NuevaVenta.jsx/VentasListado.jsx), sin necesidad de un campo aparte.
  const esNotaCredito = (payments || []).some(p => /NOTA DE CR[ÉE]DITO/i.test(p.method || ''));
  const esCredito = esNotaCredito || (payments || []).some(p => /CR[ÉE]DITO/i.test(p.method || ''));
  const tipoVenta = esNotaCredito ? 'NOTA DE CRÉDITO' : (esCredito ? 'CRÉDITO' : 'CONTADO');
  const totalNum = parseFloat(total || 0);
  const discountNum = parseFloat(discount || 0);
  const subtotalNum = parseFloat((subtotal ?? total) || 0);

  return `<!DOCTYPE html><html><head><meta charset="UTF-8">
    <style>
      hr { border: none; border-top: 1px dashed #666; margin: 6px 0; }
      .tk-empresa-nombre { text-align:center; font-weight:700; text-transform:uppercase; }
      .tk-empresa-linea { text-align:center; font-size:0.85em; color:#333; }
      .tk-doc-label { text-align:center; font-weight:700; text-transform:uppercase; font-size:0.85em; color:#333; }
      .tk-doc { text-align:center; font-weight:700; margin:6px 0; text-transform:uppercase; }
      .tk-info-row { display:flex; gap:4px; margin-bottom:2px; }
      .tk-tklbl { font-weight:600; min-width:80px; flex-shrink:0; }
      .tk-items-head { font-weight:700; text-transform:uppercase; font-size:0.9em;
        border-top:1px dashed #666; border-bottom:1px dashed #666; margin:6px 0; padding:4px 0; }
      .tk-item-block { padding:3px 0; }
      .tk-item-name { font-weight:600; }
      .tk-item-line { display:flex; justify-content:space-between; gap:6px; color:#333; }
      .tk-totales { border-top:1px dashed #666; padding-top:6px; margin-top:6px; }
      .tk-total-row { display:flex; justify-content:space-between; padding:1px 0; }
      .tk-total-row.grand { font-weight:700; font-size:1.2em; border-top:1px dashed #666; margin-top:4px; padding-top:4px; }
      ${CSS_BY_FMT[fmt] || CSS_BY_FMT['80']}
    </style>
    </head><body>
      <div class="tk-empresa-nombre">${empresa?.name || ''}</div>
      ${empresa?.address ? `<div class="tk-empresa-linea">${empresa.address}</div>` : ''}
      <hr />
      <div class="tk-doc-label">RECIBO DE VENTA</div>
      <div class="tk-doc">${serie || ''}-${fmtSeqStr(secuencia)}</div>
      <hr />
      <div>
        <div class="tk-info-row"><span class="tk-tklbl">CLIENTE:</span><span>${client_name || 'CLIENTES VARIOS'}</span></div>
        ${document_number ? `<div class="tk-info-row"><span class="tk-tklbl">DNI/RUC:</span><span>${document_number}</span></div>` : ''}
        <div class="tk-info-row"><span class="tk-tklbl">FECHA:</span><span>${fechaTxt}</span></div>
        <div class="tk-info-row"><span class="tk-tklbl">VENDEDOR:</span><span>${seller || ''}</span></div>
        <div class="tk-info-row"><span class="tk-tklbl">TIPO:</span><span>${tipoVenta}</span></div>
      </div>
      <div class="tk-items-head">DETALLE DE PRODUCTOS</div>
      ${itemsHtml}
      <div class="tk-totales">
        <div class="tk-total-row"><span>Gravada S/</span><span>${fmt2(subtotalNum)}</span></div>
        ${discountNum > 0 ? `<div class="tk-total-row"><span>Descuento S/</span><span>-${fmt2(discountNum)}</span></div>` : ''}
        <div class="tk-total-row grand"><span>TOTAL S/</span><span>${fmt2(totalNum)}</span></div>
      </div>
      <hr />
      <div style="font-size:0.9em">
        <div class="tk-info-row"><span class="tk-tklbl">SON:</span><span style="flex:1">${numberToWords(totalNum)}</span></div>
        <div class="tk-info-row"><span class="tk-tklbl">FORMA PAGO:</span><span>${formaPago}</span></div>
      </div>
      ${notes ? `<hr /><div class="tk-info-row"><span class="tk-tklbl">NOTAS:</span><span style="flex:1">${notes}</span></div>` : ''}
      <script>window.onload = () => { window.print(); };</script>
    </body></html>`;
}

const WINDOW_SIZE = { a4: 'width=650,height=800', '80': 'width=420,height=650', '60': 'width=360,height=650' };

/**
 * Abre una ventana nueva e imprime el comprobante de venta en el formato indicado.
 * @param {object} data  { empresa:{name,address}, serie, secuencia, client_name, document_number, date, seller, items, payments, total }
 * @param {'a4'|'80'|'60'} fmt
 */
export function imprimirVenta(data, fmt = '80') {
  const html = buildHtml(data, fmt);
  const win = window.open('', '_blank', WINDOW_SIZE[fmt] || WINDOW_SIZE['80']);
  if (!win) return;
  win.document.write(html);
  win.document.close();
}
